import { sb, SUPABASE_ANON_KEY, SUPABASE_FUNCTIONS_URL } from '../supabase';

// Web Telr payment (hosted page). Pay → telr-create → redirect to Telr → the buyer
// returns to /telr-return which calls telr-verify. The Telr order ref is stashed in
// sessionStorage across the redirect (survives the round-trip in the same tab), so
// no server-side pending table is needed.

async function callEdge(fn, body) {
  const { data: { session } } = await sb.auth.getSession();
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.error) {
    // The HTTP status rides the error: the return page tells "not paid" (402
    // from telr-verify) apart from "could not confirm", and the Pay buttons
    // word a 409 from telr-create differently from a network failure.
    const err = new Error(json?.error || `${fn} failed.`);
    err.status = res.status;
    if (json?.reason) err.reason = json.reason;
    throw err;
  }
  return json;
}

const stashKey = (id) => `telr:${id}`;

// Start a hosted-page payment for an order (kind 'order') or a chat invoice
// (kind 'invoice'): create the Telr order, stash its ref, redirect to Telr.
// stage: 'deposit' | 'balance' | 'full' | 'managed_fee' (the request fee).
// extraParams ride the return URL (e.g. { factory } for the request fee).
// `back` — the page the trader paid from — is added to them automatically so the
// return page can send the trader back there (TelrReturn validates it against an
// allowlist; see safeBackPath). It is appended AFTER the existing params, which
// telr-create / telr-verify and /telr-app-return never read.
export async function startTelrPayment(id, stage, kind = 'order', extraParams = {}) {
  const idParam = kind === 'invoice' ? `invoiceId=${encodeURIComponent(id)}` : `requestId=${encodeURIComponent(id)}`;
  const params = { ...(extraParams || {}) };
  if (params.back === undefined) {
    try { params.back = `${window.location.pathname}${window.location.search}`; } catch { /* ignore */ }
  }
  const extra = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('');
  const returnUrl = `${window.location.origin}/telr-return?${idParam}&stage=${encodeURIComponent(stage)}&kind=${kind}${extra}`;
  const createBody = kind === 'invoice' ? { invoiceId: id } : { requestId: id };
  const order = await callEdge('telr-create', { ...createBody, stage, platform: 'web', returnUrl });
  try { sessionStorage.setItem(stashKey(id), JSON.stringify({ ref: order.ref, stage, kind })); } catch { /* ignore */ }
  window.location.href = order.url;
}

// On the return page: recover the stashed ref and verify server-side.
// The stage travels too: telr-verify records the request fee only when told
// stage 'managed_fee' (an order stage is derived server-side from the cartid).
export async function completeTelrReturn(id) {
  let ref = null; let kind = 'order'; let stage = null;
  try {
    const raw = sessionStorage.getItem(stashKey(id));
    if (raw) { const s = JSON.parse(raw); ref = s.ref; kind = s.kind || 'order'; stage = s.stage || null; }
  } catch { /* ignore */ }
  if (!ref) throw new Error('Missing payment reference — please retry the payment.');
  const body = kind === 'invoice' ? { invoiceId: id, ref } : { requestId: id, ref };
  const result = await callEdge('telr-verify', { ...body, platform: 'web', ...(stage === 'managed_fee' ? { stage } : {}) });
  try { sessionStorage.removeItem(stashKey(id)); } catch { /* ignore */ }
  return result;
}

// ── Outcome helpers (web) ──────────────────────────────────────────────────

// telr-verify answers 402 'Payment is not in a paid state.' for a declined or
// cancelled payment: nothing was charged. Anything else is "could not confirm".
export function isNotPaidError(e) {
  return e?.status === 402 || /not in a paid state/i.test(String(e?.message || ''));
}

// Where "back" may go after Telr. Only our own pages a trader pays from, as a
// relative path — never another origin (the value arrives in a URL anyone can
// craft, so it must not become an open redirect).
const BACK_PATH = /^\/(?:managed-order\/[\w-]+|chat\/[\w-]+(?:\/[\w-]+){0,2}|dashboard)(?:\?[\w=&%.-]*)?$/;
export function safeBackPath(back) {
  const b = String(back || '');
  return BACK_PATH.test(b) ? b : '';
}

// The trader-facing wording for a payment that could not START (telr-create
// failed, or the network did). Traders read Arabic or English: zh gets English.
// Returns { title, body, detail } — detail is the server's own message, shown
// small, for support.
export function paymentStartErrorText(e, lang = 'ar') {
  const ar = lang === 'ar';
  const msg = String(e?.message || '');
  let title; let body;
  if (e?.status === 409 && /already (paid|settled)/i.test(msg)) {
    title = ar ? 'هذا المبلغ مدفوع مسبقاً' : 'This has already been paid';
    body = ar ? 'لا حاجة للدفع مرة أخرى. حدّث الصفحة لترى الحالة الجديدة.' : 'There is nothing more to pay. Refresh the page to see the update.';
  } else if (e?.status === 409) {
    title = ar ? 'لا يمكن الدفع إلكترونياً الآن' : "This can't be paid online right now";
    body = ar ? 'لم يُخصم أي مبلغ. تواصل مع دعم مَعبر وسنكمل الدفع معك.' : "Nothing was charged. Contact Maabar support and we'll complete the payment with you.";
  } else if (e?.status === 401) {
    title = ar ? 'انتهت جلستك' : 'Your session has expired';
    body = ar ? 'لم يُخصم أي مبلغ. سجّل الدخول مرة أخرى ثم أعد المحاولة.' : 'Nothing was charged. Sign in again, then try again.';
  } else {
    title = ar ? 'تعذّر فتح صفحة الدفع' : "We couldn't open the payment page";
    body = ar ? 'لم يُخصم أي مبلغ. حاول مرة أخرى بعد قليل.' : 'Nothing was charged. Please try again in a moment.';
  }
  return { title, body, detail: msg };
}

// One string, for the call sites that can only alert().
export function paymentStartErrorAlert(e, lang = 'ar') {
  const { title, body, detail } = paymentStartErrorText(e, lang);
  return `${title}\n${body}${detail ? `\n\n(${detail})` : ''}`;
}
